import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { chunk, isUndefined } from 'lodash';

import { MarketStatuses } from 'constants/app';
import { NumberOfPairs } from 'constants/marketTypes';
import useMarketStatusAndLockIcon from 'hooks/useMarketStatusAndLockIcon';
import { useUpdateEffect } from 'hooks/useUpdateEffect';
import { getLoggedInStatusState } from 'redux/modules/auth/selectors';
import {
  getMarketPricesFirstKeyLineHandicap,
  getMarketPricesFirstKeyLineId,
  getMarketPricesIsBettingEnabled,
  getMarketPricesSecondKeyLineHandicap,
  getMarketPricesSecondKeyLineId,
  getStatusByMarketPricesId
} from 'redux/modules/marketsPrices/selectors';
import { IMarket } from 'types/markets';
import { getIsSelectionEqualsToKeyLine, getMarketTypes } from 'utils/market';

const useMarketWithManySelections = ({
  market,
  isMobileEditOfferLine,
  mobileEditOfferLine
}: {
  market: IMarket;
  isMobileEditOfferLine?: boolean;
  mobileEditOfferLine?: number | null;
}) => {
  const { marketId, description, runners, closedDate } = market;

  const isLoggedIn = useSelector(getLoggedInStatusState);
  const bettingEnabled = useSelector(getMarketPricesIsBettingEnabled(marketId));
  const marketStatus = useSelector(getStatusByMarketPricesId(marketId));
  const firstKeyLineId = useSelector(getMarketPricesFirstKeyLineId(marketId));
  const firstKeyLineHandicap = useSelector(getMarketPricesFirstKeyLineHandicap(marketId));
  const secondKeyLineId = useSelector(getMarketPricesSecondKeyLineId(marketId));
  const secondKeyLineHandicap = useSelector(getMarketPricesSecondKeyLineHandicap(marketId));

  const [currentSelectionsLine, setCurrentSelectionsLine] = useState(0);
  const [isSelectionUpdated, setIsSelectionUpdated] = useState(false);

  const { isOpeningSoonStatus } = useMarketStatusAndLockIcon(bettingEnabled, marketStatus);

  const { isHandicapDoubleLine } = getMarketTypes(market.description.marketType, market.description.bettingType);
  const isClosedStatus = marketStatus === MarketStatuses.CLOSED;
  const isMarketClosed = isClosedStatus || !!closedDate;

  const { group: groupSize } =
    NumberOfPairs[description.marketType as keyof typeof NumberOfPairs] || NumberOfPairs.default;

  const groupedRunners = useMemo(() => chunk(runners, groupSize), [groupSize, runners]);

  const keyLineNumber = useMemo(() => {
    if (isMarketClosed && isUndefined(firstKeyLineId)) return 0;

    return groupedRunners.findIndex(linesGroup => {
      const isFirstLineEqualKeyLine = getIsSelectionEqualsToKeyLine({
        selectionId: linesGroup[0].selectionId,
        handicap: linesGroup[0].handicap,
        firstKeyLineId,
        firstKeyLineHandicap,
        secondKeyLineId,
        secondKeyLineHandicap
      });

      if (linesGroup.length === 2) {
        const isSecondLineEqualKeyLine = getIsSelectionEqualsToKeyLine({
          selectionId: linesGroup[1].selectionId,
          handicap: linesGroup[1].handicap,
          firstKeyLineId,
          firstKeyLineHandicap,
          secondKeyLineId,
          secondKeyLineHandicap
        });

        return isFirstLineEqualKeyLine && isSecondLineEqualKeyLine;
      }

      return isFirstLineEqualKeyLine;
    });
  }, [firstKeyLineHandicap, firstKeyLineId, groupedRunners, secondKeyLineHandicap, secondKeyLineId, isMarketClosed]);

  const defaultLineNumber = useMemo(() => {
    if (isSelectionUpdated && currentSelectionsLine >= 0) return currentSelectionsLine;
    return keyLineNumber;
  }, [isSelectionUpdated, currentSelectionsLine, keyLineNumber]);

  const isDefaultLineNumber = defaultLineNumber >= 0;
  const isLineNumber = isMobileEditOfferLine || isDefaultLineNumber;

  const selectedRunners = isHandicapDoubleLine ? groupedRunners[currentSelectionsLine] ?? null : null;

  /**
   * Update currentSelectionsLine when key line changes
   */
  useUpdateEffect(() => {
    if (!isSelectionUpdated && keyLineNumber >= 0) {
      setCurrentSelectionsLine(keyLineNumber);
    }
  }, [keyLineNumber]);

  /**
   * Update isSelectionUpdated when currentSelectionsLine changes
   */
  useUpdateEffect(() => {
    if (currentSelectionsLine !== defaultLineNumber) {
      setIsSelectionUpdated(true);
    }
  }, [currentSelectionsLine]);

  /**
   * Set initial currentSelectionsLine
   */
  useEffect(() => {
    if (isLineNumber) {
      const newLine = mobileEditOfferLine && mobileEditOfferLine > -1 ? mobileEditOfferLine : defaultLineNumber;

      setCurrentSelectionsLine(newLine);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLineNumber, defaultLineNumber]);

  /**
   * Reset currentSelectionsLine when user logs in
   */
  useUpdateEffect(() => {
    if (isLoggedIn) {
      setIsSelectionUpdated(false);
      setCurrentSelectionsLine(keyLineNumber);
    }
  }, [isLoggedIn]);

  return {
    selectedRunners,
    defaultLineNumber,
    keyLineNumber,
    currentSelectionsLine,
    isHandicapDoubleLine,
    isOpeningSoonStatus,
    isSelectionUpdated,
    setCurrentSelectionsLine
  };
};

export default useMarketWithManySelections;
